/**
 * Marks the MARKET_PRICE cell of every row an added-instrument check has locked: in place of a price
 * it shows a spinner while the check is out, or an icon, each with a tooltip saying why the row has no price
 * and where the user goes about it — the icon alone, with no "—" beside it. It is the cell the user
 * looks at for a price, so it is where a missing one has to explain itself — the Issues column may
 * sit out of view.
 */
import {
	AutoTooltip,
	CircularProgressBar,
	colorBySeverity,
	IconTooltip,
	Row,
	TooltipContent,
} from "@mdotm/shadcn/legacy/components";
import type { ColDef } from "ag-grid-community";
import type { CustomCellRendererProps } from "ag-grid-react";
import { useTranslation } from "react-i18next";
import type { Instrument, NewInstrumentCheck } from "../../const";
import { withCellMarker } from "../withCellMarker";
import { isLocked } from "./checks";

const TOOLTIP_KEYS = {
	failed: "EDIT_COMPOSITION.NEW_INSTRUMENTS.PRICE_CELL_FAILED",
	fixable: "EDIT_COMPOSITION.NEW_INSTRUMENTS.PRICE_CELL_FIXABLE",
	unfixable: "EDIT_COMPOSITION.NEW_INSTRUMENTS.PRICE_CELL_UNFIXABLE",
} as const satisfies Partial<Record<NewInstrumentCheck["status"], string>>;

function CheckingSpinnerTrigger({ innerRef }: { innerRef: React.Ref<HTMLSpanElement> }): React.JSX.Element {
	return (
		<span ref={innerRef} className="flex">
			<CircularProgressBar
				className="w-3"
				value="indeterminate"
				data-qualifier="CompositionEditor/NewInstrumentPriceChecking"
			/>
		</span>
	);
}

export function CheckMarkerCell({ data, valueFormatted }: CustomCellRendererProps<Instrument>): React.JSX.Element {
	const { t } = useTranslation();

	// Only `withCellMarker` hands this cell a row, and only a locked one; this narrows its status.
	if (data == null || !isLocked(data)) {
		return <>{valueFormatted ?? ""}</>;
	}
	const { status } = data.newInstrumentCheck;

	return (
		<Row className="h-full w-full" justifyContent="end" alignItems="center" gap={4}>
			{status === "checking" ? (
				// IconTooltip's own tooltip, around the spinner instead of an icon: the cell keeps showing
				// the check is out, and the explanation looks like the other three states'.
				<AutoTooltip
					position="top"
					align="endToEnd"
					severity="info"
					overrideColor={colorBySeverity.info}
					hideArrow
					trigger={CheckingSpinnerTrigger}
				>
					<TooltipContent>
						<span data-qualifier="CompositionEditor/NewInstrumentPriceTooltip">
							{t("EDIT_COMPOSITION.NEW_INSTRUMENTS.PRICE_CELL_CHECKING")}
						</span>
					</TooltipContent>
				</AutoTooltip>
			) : (
				<IconTooltip severity={status === "unfixable" ? "info" : "warning"} iconSize={16} hideArrow>
					<TooltipContent>
						<span data-qualifier="CompositionEditor/NewInstrumentPriceTooltip">{t(TOOLTIP_KEYS[status])}</span>
					</TooltipContent>
				</IconTooltip>
			)}
		</Row>
	);
}

/** Layers the check marker onto the MARKET_PRICE column. */
export function withCheckMarker(column: ColDef<Instrument>): ColDef<Instrument> {
	return withCellMarker(column, { applies: isLocked, Marker: CheckMarkerCell });
}
